// Copyright (c) Ping. All rights reserved.
//
// [#2] Sender side of linking history-transfer. After the existing device
// admits a newly-linked device to a conversation (addMembers/admitDeviceToChats),
// the new device becomes its own MLS leaf but can't decrypt pre-link messages
// (forward secrecy). So we ship it the DECRYPTED history it's now entitled to:
// gather the stored raw AppEvent bytes per conversation, HPKE-seal + SIGN them
// to the new device (MessagingClient.sealHistoryBundle — confidential to the
// QR ephemeral key, authenticated by the account identity), and POST to the
// server-blind history-transfer inbox. The new device opens + replays them.
//
// Forward-secrecy preserved: we share PLAINTEXT the existing device already had,
// never MLS epoch keys. Best-effort: a per-conversation failure is logged + skipped.

import { MessagingClient } from 'ping-openmls-sdk';

import { urlB64NoPadToBytes } from '@ping/ui';

import { log } from '@core/log/log';
import { exportUserIdentityForBackup } from '@core/network/deviceKeys';
import { db } from '@core/persistence/db';

import { postHistoryTransfer } from './historyTransfersApi';

// Bound the bundle: 30-day window, newest-N messages per conversation, so a
// single sealed transfer stays well under the server's per-row cap.
const HISTORY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_EVENTS_PER_CONVERSATION = 200;

function hex(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}

function hexToBytes(h: string): Uint8Array {
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(h.slice(i * 2, i * 2 + 2), 16);
  return out;
}

interface SealableClient {
  // Static on the SDK class; typed loosely so we don't depend on the SDK's
  // exact d.ts shape from the app layer.
  sealHistoryBundle(
    bundle: unknown,
    recipientPub: Uint8Array,
    senderIdentitySecret: Uint8Array,
  ): Promise<Uint8Array>;
}

/**
 * Ship `conversationIds` worth of decrypted history to the just-linked device.
 *
 * @param recipientDeviceIdB64Url new device id (base64url) — inbox address.
 * @param recipientPub            new device's ephemeral X25519 pubkey (from the QR).
 * @param selfDeviceIdBytes       this device's id (informational on the bundle).
 * @param conversationIds         conversation ids (16-byte) the device was admitted to.
 */
export async function sendLinkingHistoryToDevice(params: {
  recipientDeviceIdB64Url: string;
  recipientPub: Uint8Array;
  selfDeviceIdBytes: Uint8Array;
  conversationIds: Uint8Array[];
}): Promise<void> {
  const { recipientDeviceIdB64Url, recipientPub, selfDeviceIdBytes, conversationIds } = params;
  if (conversationIds.length === 0) return;

  // The account identity Ed25519 seed signs each bundle so the recipient can
  // verify the sender against the user-identity pubkey it holds from linking.
  let identitySecret: Uint8Array;
  try {
    const userIdentity = await exportUserIdentityForBackup();
    const d = userIdentity.jwk.d;
    if (typeof d !== 'string') throw new Error('identity jwk missing d');
    identitySecret = urlB64NoPadToBytes(d);
  } catch (err) {
    log.warn({
      event: 'linking.history.identitySecretUnavailable',
      message: (err as Error).message,
    });
    return;
  }

  const MC = MessagingClient as unknown as SealableClient;
  const recipientIdBytes = urlB64NoPadToBytes(recipientDeviceIdB64Url);
  const now = Date.now();
  const cutoff = now - HISTORY_WINDOW_MS;
  let sentConversations = 0;

  // PASS 1 — build every conversation's sealed bundle (skipping the ones that
  // reconstruct to nothing) BEFORE posting any. The receiver's true-% banner
  // needs the exact denominator (`batch_total`), and that's only knowable once
  // we know how many conversations actually produce a non-empty bundle.
  const pending: Array<{ convPrefix: string; sealed: Uint8Array }> = [];

  for (const convId of conversationIds) {
    try {
      const convHex = hex(convId);
      // Skip conversations the user has soft-deleted locally — replaying their
      // sealed history bundle would RESURRECT the deleted chat on the freshly-
      // linked device (the bundle replays directly into Dexie, independent of
      // MLS admission). A linked device should mirror what the user sees.
      const convRow = await db.conversations.get(convHex);
      if (convRow !== undefined && (convRow.deletedAt ?? 0) > 0) continue;
      const rows = (
        await db.messages.where('conversationId').equals(convHex).toArray()
      )
        .filter((m) => !m.deletedAt && m.sentAt >= cutoff)
        .sort((a, b) => a.sentAt - b.sentAt);
      const recent = rows.slice(-MAX_EVENTS_PER_CONVERSATION);
      if (recent.length === 0) continue;

      const events: Array<{
        event_id: Uint8Array;
        wall_ms: number;
        payload: Uint8Array;
        author: string;
      }> = [];
      for (const m of recent) {
        // Web never stores `appEventBytes` on OUTGOING rows (only the incoming
        // path sets it), so a filter on it dropped every self-sent message.
        // Use the stored bytes when present (received rows); otherwise
        // RECONSTRUCT the SAME envelope JSON the live path would have stored,
        // so the receiver's SHA-256(payload) content-hash dedup still matches:
        //   text  -> {kind:'com.ping.text', payload:{body: m.body}}
        //   media -> m.body already IS the full envelope JSON (voice/gif/image/file)
        //   receipts / no body -> not reconstructable, skip.
        const payload = reconstructPayload(m.body, m.appEventBytes);
        if (payload === undefined) continue;
        events.push({
          // The real app event_id lives inside the encoded envelope; this
          // bundle id is informational (replay dedups by the decoded content
          // hash), so a stable 16-byte slice of the row's contentHash suffices.
          event_id: hexToBytes(m.contentHash).slice(0, 16),
          wall_ms: m.sentAt,
          payload,
          // Carry the original author so the linked device renders self-vs-peer
          // bubbles correctly — the payload envelope itself has no author (that
          // lives at the MLS layer). Self-sent rows store the auth `account_id`
          // here (senderAccountId === identity.accountId); received rows store
          // the peer's id. The recipient's self-check (author === my
          // account_id) then matches.
          author: m.senderAccountId,
        });
      }
      if (events.length === 0) continue;

      const bundle = {
        v: 1,
        sender_device_id: selfDeviceIdBytes,
        recipient_device_id: recipientIdBytes,
        conversation_id: convId,
        window_start_ms: cutoff,
        window_end_ms: now,
        events,
      };

      const sealed = await MC.sealHistoryBundle(bundle, recipientPub, identitySecret);
      pending.push({ convPrefix: convHex.slice(0, 8), sealed });
    } catch (err) {
      log.warn({
        event: 'linking.history.sendConversationFailed',
        message: (err as Error).message,
      });
    }
  }

  // PASS 2 — post each sealed bundle with `batch_total` = the count of
  // non-empty bundles, so the receiver learns the exact denominator for the
  // "Syncing conversations… N%" banner.
  const batchTotal = pending.length;
  for (const p of pending) {
    try {
      await postHistoryTransfer(recipientDeviceIdB64Url, p.sealed, batchTotal);
      sentConversations += 1;
    } catch (err) {
      log.warn({
        event: 'linking.history.sendConversationFailed',
        message: (err as Error).message,
      });
    }
  }
  // Wipe the in-memory identity seed copy.
  identitySecret.fill(0);
  log.info({
    event: 'linking.history.sent',
    conversations: sentConversations,
    total: conversationIds.length,
    batchTotal,
    recipientPrefix: recipientDeviceIdB64Url.slice(0, 8),
  });
}

/**
 * Inverse of `pendingLinkingHistory.ts` `bodyForReplay`: turn a stored message
 * row back into the raw AppEvent envelope bytes the live path would have stored,
 * so the receiver's `SHA-256(payload)` content-hash dedup matches.
 *  - bytes present (received rows)        -> use as-is
 *  - text rows (`body` = raw text)        -> {kind:'com.ping.text', payload:{body}}
 *  - media rows (`body` = full envelope)  -> the JSON body bytes verbatim
 *  - no body / receipts                   -> undefined (caller skips)
 */
function reconstructPayload(
  body: string | undefined,
  appEventBytes: Uint8Array | undefined,
): Uint8Array | undefined {
  if (appEventBytes !== undefined) return appEventBytes;
  if (body === undefined || body.length === 0) return undefined;
  // Media kinds store the FULL envelope JSON as `body` already — emit verbatim.
  try {
    const parsed = JSON.parse(body) as { kind?: unknown };
    if (
      parsed.kind === 'com.ping.voice' ||
      parsed.kind === 'com.ping.gif' ||
      parsed.kind === 'com.ping.image' ||
      parsed.kind === 'com.ping.file'
    ) {
      return new TextEncoder().encode(body);
    }
  } catch {
    /* not JSON — a plain-text body, handled below */
  }
  // Plain-text row: wrap the raw text in the canonical text envelope.
  return new TextEncoder().encode(
    JSON.stringify({ kind: 'com.ping.text', payload: { body } }),
  );
}
